import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { BaseApiService } from '@core/api/base-api.service';
import { AccountDto, AccountPostPayload } from '../models/rbac.model';

function unwrapAccount(raw: any): AccountDto {
  if (raw?.account) {
    return { ...raw.account, tenantAccounts: raw.tenantAccounts ?? [] };
  }
  return raw as AccountDto;
}

@Injectable({ providedIn: 'root' })
export class AccountService {
  private readonly api = inject(BaseApiService);
  private readonly base = 'v1/admin/rbac';

  getAccounts(
    tenantUuid?: string,
    search?: string,
    page: number = 0,
    size: number = 20,
    filters: Record<string, unknown[]> = {},
  ): Observable<{ accounts: readonly AccountDto[]; totalElements: number }> {
    let params = new HttpParams();
    if (search?.trim()) params = params.set('q', search.trim());
    params = params.set('size', size.toString());
    params = params.set('page', page.toString());

    for (const [key, values] of Object.entries(filters)) {
      for (const value of values) {
        params = params.append(key, String(value));
      }
    }

    const path = tenantUuid
      ? `${this.base}/tenants/${tenantUuid}/accounts`
      : `${this.base}/accounts`;

    return this.api.get<any>(path, { params }).pipe(
      map((res) => {
        const data = res?.data ?? res;
        const arr = Array.isArray(data) ? data : Array.isArray(data?.content) ? data.content : [];
        const totalElements =
          typeof data?.totalElements === 'number' ? data.totalElements : arr.length;
        return { accounts: (arr as any[]).map(unwrapAccount), totalElements };
      }),
    );
  }

  getAccount(accountUuid: string): Observable<AccountDto> {
    return this.api
      .get<any>(`${this.base}/accounts/${accountUuid}`, {
        params: new HttpParams().set('includeTenants', 'true'),
      })
      .pipe(map(unwrapAccount));
  }

  createAccount(payload: AccountPostPayload): Observable<AccountDto> {
    return this.api.post<any>(`${this.base}/accounts`, payload).pipe(map(unwrapAccount));
  }

  updateAccount(accountUuid: string, payload: AccountPostPayload): Observable<AccountDto> {
    return this.api
      .put<any>(`${this.base}/accounts/${accountUuid}`, payload)
      .pipe(map(unwrapAccount));
  }

  deleteAccount(accountUuid: string): Observable<void> {
    return this.api.delete(`${this.base}/accounts/${accountUuid}`).pipe(map(() => void 0));
  }

  resetPassword(accountUuid: string): Observable<void> {
    return this.api
      .put<void>(`${this.base}/accounts/${accountUuid}/reset-password`, {})
      .pipe(map(() => void 0));
  }

  assignTenant(accountUuid: string, tenantUuid: string, tenantAdmin = false): Observable<void> {
    return this.api
      .post<void>(`${this.base}/accounts/${accountUuid}/tenants`, { tenantUuid, tenantAdmin })
      .pipe(map(() => void 0));
  }

  unassignTenant(accountUuid: string, tenantUuid: string): Observable<void> {
    return this.api
      .delete(`${this.base}/accounts/${accountUuid}/tenants/${tenantUuid}`)
      .pipe(map(() => void 0));
  }

  updateTenantAssignment(
    accountUuid: string,
    tenantUuid: string,
    payload: { tenantAdmin?: boolean; blocked?: boolean },
  ): Observable<void> {
    return this.api
      .put<void>(`${this.base}/accounts/${accountUuid}/tenants/${tenantUuid}`, payload)
      .pipe(map(() => void 0));
  }

  blockAccount(accountUuid: string): Observable<void> {
    return this.api
      .post<void>(`${this.base}/accounts/${accountUuid}/block`, {})
      .pipe(map(() => void 0));
  }

  unblockAccount(accountUuid: string): Observable<void> {
    return this.api
      .post<void>(`${this.base}/accounts/${accountUuid}/unblock`, {})
      .pipe(map(() => void 0));
  }
}
